import { getApiClient, type GraphQLResult } from './apiClient';

/**
 * Tầng thanh toán (P9) — cổng, bảng giá, bậc bonus, đối soát.
 *
 * apiClient lấy TÊN FIELD ĐẦU TIÊN trong query làm tên operation rồi POST tới
 * /api/v1/<op>, nên các "query GraphQL" dưới đây chỉ cần đúng tên op và tên
 * biến; phần selection set không ảnh hưởng gì tới dữ liệu trả về.
 */

export interface SecretState {
  set: boolean;
  /** 4 ký tự cuối digest — đủ để biết khoá trên server có phải khoá mình vừa đặt. */
  fingerprint: string | null;
}

export interface PaymentProvider {
  id: string;
  kind: string;
  displayName: string;
  currency: string;
  enabled: boolean;
  sortOrder: number;
  config: Record<string, string>;
  secrets: Record<string, SecretState>;
  updatedAt: number;
  updatedBy: string | null;
}

export interface ProviderCheck {
  key: string;
  ok: boolean;
  detail: string;
}

export interface ProviderTest {
  id: string;
  kind: string;
  enabled: boolean;
  ready: boolean;
  blocking: string[];
  checks: ProviderCheck[];
}

export interface PricingRow {
  licenseType: string;
  durationDays: number;
  pricePerDevice: number;
  isActive: boolean;
}

export interface BonusTier {
  minAmountUsdt: number;
  bonusPercent: number;
  isActive: boolean;
}

export interface ProcessConfig {
  pricePerLine: number;
  maxLines: number;
  jobTimeoutMs: number;
  queueTimeoutMs: number;
  maxAttempts: number;
  /** BẬT: job cạn maxAttempts -> chờ xử lý tay. TẮT: -> fail luôn. */
  manualModeEnabled: boolean;
  maintenanceEnabled: boolean;
  maintenanceMessage: string | null;
  maintenanceUntil: number | null;
}

export interface BotAccessUser {
  telegramId: string;
  status: 'pending' | 'allowed' | 'blocked';
  username: string | null;
  firstName: string | null;
  requestedAt: number;
  decidedAt: number | null;
  updatedAt: number;
  /** Tổng point đã nạp (CONFIRMED). */
  deposited?: number;
  /** Số dư point còn lại. */
  balance?: number;
  /** Có ít nhất 1 API key đang dùng được. */
  hasApiKey?: boolean;
}

export interface AdminDeposit {
  code: string;
  email: string;
  depositId: string;
  method: string;
  status: string;
  amountUsdt: number;
  bonusPercent: number;
  expectedPoints: number;
  actualAmountUsdt: number | null;
  pointsCredited: number | null;
  txId: string | null;
  payerRef: string | null;
  payerName: string | null;
  note: string | null;
  expiresAt: number;
  createdAt: number;
}

export interface UnmatchedPayment {
  id: number;
  source: string;
  externalId: string;
  note: string | null;
  amount: number;
  currency: string | null;
  payerRef: string | null;
  payerName: string | null;
  reason: string;
  resolved: number;
  createdAt: number;
}

/**
 * `undefined` = không gửi field đó (server giữ nguyên).
 * `null` cho một secret = XOÁ khoá đó. Chuỗi rỗng = giữ nguyên — khớp với ô nhập
 * trên UI luôn để trống vì không bao giờ đổ khoá cũ ra màn hình.
 */
export interface SweepRow {
  hdIndex: number;
  email: string;
  address: string;
  usdt: number;
  needsGas: boolean;
  worthIt: boolean;
  willSweep: boolean;
  sweptAt: number | null;
}

export interface SweepStatus {
  providerId: string;
  networkLabel: string;
  native: string;
  sweepTo: string | null;
  walletMatches: boolean;
  walletError: string | null;
  gasFunder: { address: string; balance: number; perFund: number; refillsLeft: number | null } | null;
  gasPerSweep: number;
  rows: SweepRow[];
  totals: { sweepable: number; willSweep: number; skippedTooSmall: number };
  alertUsdt: number | null;
  overAlert: boolean;
  /** Có mặt khi không đọc được mạng — các trường trên khi đó không dùng được. */
  error?: string;
}

export interface SweepOutcome {
  hdIndex: number;
  address: string;
  ok: boolean;
  usdt: number;
  txHash: string | null;
  message: string;
}

export interface ProviderPatch {
  id: string;
  displayName?: string;
  enabled?: boolean;
  config?: Record<string, string>;
  secrets?: Record<string, string | null>;
}

const q = (op: string, vars: string[] = []): string => {
  const sig = vars.length ? `(${vars.map((v) => `$${v}: String`).join(', ')})` : '';
  const args = vars.length ? `(${vars.map((v) => `${v}: $${v}`).join(', ')})` : '';
  return `query Op${sig} { ${op}${args} { __typename } }`;
};

const unwrap = <T>(res: GraphQLResult<any>, key: string): T => {
  if (res.errors?.length) throw new Error(res.errors[0].message);
  return res.data?.[key] as T;
};

const call = async <T>(op: string, variables: Record<string, any> = {}): Promise<T> => {
  const res = await getApiClient().graphql({ query: q(op, Object.keys(variables)), variables });
  return unwrap<T>(res, op);
};

export interface ManualJob {
  jobId: string;
  batchId: string;
  lineNo: number;
  email: string;
  /** input giải mã: email|password hoặc email|password|recovery */
  input: string;
  fields: string[];
  status: string;
  messageCode: string | null;
  message: string | null;
  attempts: number;
  imei: string | null;
  requestedAt: number;
  startedAt: number | null;
  /** 'partner' = đăng ký qua API key (chỉ trả kết quả 1 lần); khác = bot. */
  source?: string | null;
}

export interface AuditJob {
  jobId: string;
  batchId: string;
  lineNo: number;
  email: string;
  /** Người gửi job: "telegramId(tên)" (null nếu không rõ nguồn). */
  requester: string | null;
  status: string;
  messageCode: string | null;
  message: string | null;
  serialNo: string | null;
  imei: string | null;
  attempts: number;
  /** Link kết quả (rỗng/null nếu fail hoặc chưa có). */
  url: string | null;
  /** Output đầy đủ đã giải mã (object) hoặc null. KHÔNG có raw input. */
  output: unknown;
  requestedAt: number;
  startedAt: number | null;
  completedAt: number | null;
}

/** Một email (gộp mọi lần thử) ở trang Tất cả job. */
export interface AuditGroup {
  email: string;
  /** Người gửi của lần MỚI NHẤT: "telegramId(tên)" (null nếu không rõ nguồn). */
  requester: string | null;
  /** Trạng thái của lần thử mới nhất. */
  latestStatus: string;
  /** requestedAt của lần mới nhất. */
  latestAt: number;
  /** Tổng số lần thử của email này. */
  attempts: number;
  /** Toàn bộ các lần thử, MỚI NHẤT trước. */
  history: AuditJob[];
}

export interface ApiKey {
  id: string;
  prefix: string;
  ownerTg: string;
  label: string | null;
  enabled: number;
  createdAt: number;
  createdBy: string | null;
  lastUsedAt: number | null;
  revokedAt: number | null;
}

export interface PartnerWebhook {
  ownerTg: string;
  url: string;
  enabled: number;
  createdAt: number;
  updatedAt: number;
  lastStatus: number | null;
  lastAt: number | null;
}

export interface JobStats {
  /** Cần xử lý tay (pending_manual). */
  pendingManual: number;
  success: number;
  failed: number;
  /** Đã xử lý = success + failed. */
  processed: number;
  queued: number;
  running: number;
  rejected: number;
  total: number;
  /** Phát sinh hôm nay (giờ VN): success/failed theo completedAt, pending_manual theo requestedAt. */
  pendingManualToday: number;
  successToday: number;
  failedToday: number;
  processedToday: number;
}

export const paymentService = {
  listProviders: () =>
    call<{
      providers: PaymentProvider[];
      /** Allowlist của server, theo `kind`. Trang dùng để không bỏ sót field nào. */
      configFields: Record<string, string[]>;
      secretFields: Record<string, string[]>;
    }>('getPaymentProviders'),

  saveProvider: (patch: ProviderPatch) =>
    call<{ result: boolean; provider: PaymentProvider }>('setPaymentProvider', patch),

  testProvider: (id: string) => call<ProviderTest>('testPaymentProvider', { id }),

  /** Tải ảnh QR lên. `base64` nhận cả data URL lẫn base64 trần. */
  uploadQr: (providerId: string, base64: string) =>
    call<{ result: boolean; bytes: number; contentType: string; provider: PaymentProvider }>(
      'setPaymentQr',
      { providerId, base64 },
    ),

  removeQr: (providerId: string) =>
    call<{ result: boolean; provider: PaymentProvider }>('removePaymentQr', { providerId }),

  getPricing: (scope?: 'agentx' | 'bot') =>
    call<{ pricing: PricingRow[]; tiers: BonusTier[]; rateBasisDays: number; scope?: string }>(
      'getPaymentPricing',
      scope ? { scope } : {},
    ),

  setPrice: (row: { licenseType: string; durationDays: number; pricePerDevice: number; isActive?: boolean }) =>
    call<{ result: boolean; affectsUpgradeRate: boolean; pricing: PricingRow[] }>('setLicensePrice', row),

  // scope phân tách bonus bot vs AgentX (topup license) trên cùng bảng.
  setTier: (tier: { minAmountUsdt: number; bonusPercent: number; isActive?: boolean; scope?: 'agentx' | 'bot' }) =>
    call<{ result: boolean; tiers: BonusTier[] }>('setBonusTier', tier),

  removeTier: (minAmountUsdt: number, scope?: 'agentx' | 'bot') =>
    call<{ result: boolean; tiers: BonusTier[] }>('removeBonusTier', { minAmountUsdt, ...(scope ? { scope } : {}) }),

  // Cấu hình job automation của bot (giá/dòng, trần dòng, timeout, số lần thử,
  // chế độ bảo trì).
  getProcessConfig: () => call<ProcessConfig>('getProcessConfig'),

  // Allowlist người dùng bot (bot_access).
  listBotAccess: (status?: 'pending' | 'allowed' | 'blocked') =>
    call<{ users: BotAccessUser[] }>('listBotAccess', status ? { status } : {}),

  setBotAccess: (telegramId: string, status: 'pending' | 'allowed' | 'blocked') =>
    call<{ result: boolean; user: BotAccessUser }>('setBotAccess', { telegramId, status }),

  // Tra nhanh 1 user bot theo tgId (dùng auto-điền nhãn khi cấp API key).
  getBotUser: (telegramId: string) =>
    call<{ user: { telegramId: string; username: string | null; firstName: string | null; status: string } | null }>(
      'getBotUser',
      { telegramId },
    ),

  // Gửi thông báo chung cho MỌI user bot đã cấp phép. Trả số user đã gửi.
  broadcastBotMessage: (message: string) =>
    call<{ result: boolean; broadcast: number }>('adminBroadcastBotMessage', { message }),

  // Điều chỉnh point THẲNG cho user bot theo tgId (không gắn đơn, không bonus). Ra
  // tiền thật — UI phải xác nhận trước. `amount` dương = cộng (server đẩy thông báo
  // "đã cộng point"), âm = trừ (từ chối nếu không đủ số dư, không gửi thông báo).
  grantBotPoints: (telegramId: string, amount: number, note?: string) =>
    call<{ result: boolean; balance: number; amount: number; deducted?: number; telegramId: string }>('adminGrantBotPoints', {
      telegramId,
      amount,
      ...(note ? { note } : {}),
    }),

  // Chỉ field truyền vào mới đổi (merge phía server). `broadcast` = đẩy thông báo
  // bảo trì cho mọi user đã cấp phép (mặc định server tự bật khi vừa bật bảo trì).
  setProcessConfig: (
    cfg: {
      pricePerLine?: number;
      maxLines?: number;
      jobTimeoutMs?: number;
      queueTimeoutMs?: number;
      maxAttempts?: number;
      manualModeEnabled?: boolean;
      maintenanceEnabled?: boolean;
      maintenanceMessage?: string | null;
      maintenanceUntil?: number | null;
    },
    broadcast?: boolean,
  ) =>
    call<{ result: boolean; config: ProcessConfig; broadcast: number }>(
      'setProcessConfig',
      broadcast === undefined ? cfg : { ...cfg, broadcast },
    ),

  // Job "cần xử lý tay" (pending_manual): lỗi máy đã cạn số lần thử. Trả input GIẢI MÃ.
  listManualJobs: (opts: { limit?: number; offset?: number; includeFailed?: boolean } = {}) =>
    call<{ jobs: ManualJob[] }>('listManualJobs', opts),

  // Admin nhập kết quả tay: success + output(link) → user nhận link; failed → hoàn tiền.
  // Job API đã failed → chỉ lưu (saved=true), không gửi; tái dùng khi partner gửi lại email.
  setJobManualResult: (jobId: string, status: 'success' | 'failed', output?: string, message?: string) =>
    call<{ result: boolean; status: string; recharged?: boolean; saved?: boolean }>('setJobManualResult', {
      jobId,
      status,
      ...(output !== undefined ? { output } : {}),
      ...(message !== undefined ? { message } : {}),
    }),

  // Reset job pending_manual về hàng đợi để runner chạy lại từ đầu (attempts=0).
  requeueManualJob: (jobId: string) => call<{ result: boolean }>('requeueManualJob', { jobId }),

  // Audit toàn bộ job GỘP THEO EMAIL (không trả raw input): mỗi email 1 nhóm với
  // người gửi + status mới nhất + history đầy đủ. total = số email (trang đầu), -1 trang sau.
  listAllJobs: (opts: { status?: string; email?: string; requester?: string; limit?: number; offset?: number } = {}) =>
    call<{ groups: AuditGroup[]; total: number }>('listAllJobs', opts),

  // Thống kê job theo trạng thái (cho thanh header trang Bot Jobs).
  jobStats: () => call<JobStats>('jobStats'),

  // Partner API keys: key gắn với 1 tgId (ownerTg). createApiKey trả key ĐẦY ĐỦ 1 lần.
  listApiKeys: () => call<{ keys: ApiKey[] }>('listApiKeys'),
  // Plan B: cấp key sinh LUÔN webhook token cho ownerTg (trả webhookToken, hiện 1 lần).
  createApiKey: (ownerTg: string, label?: string) =>
    call<{ key: string; row: ApiKey; webhookToken: string }>('createApiKey', { ownerTg, ...(label ? { label } : {}) }),
  setApiKeyEnabled: (id: string, enabled: boolean) =>
    call<{ result: boolean }>('setApiKeyEnabled', { id, enabled }),
  revokeApiKey: (id: string) => call<{ result: boolean }>('revokeApiKey', { id }),

  // Partner webhooks: token tạo sẵn cùng API key; đây chỉ đặt URL / xem / xoay token.
  listWebhooks: () => call<{ webhooks: PartnerWebhook[] }>('listWebhooks'),
  setWebhookUrl: (ownerTg: string, url: string) => call<{ result: boolean }>('setWebhookUrl', { ownerTg, url }),
  revealWebhookToken: (ownerTg: string) => call<{ token: string }>('revealWebhookToken', { ownerTg }),
  rotateWebhookToken: (ownerTg: string) => call<{ token: string }>('rotateWebhookToken', { ownerTg }),
  setWebhookEnabled: (ownerTg: string, enabled: boolean) =>
    call<{ result: boolean }>('setWebhookEnabled', { ownerTg, enabled }),

  listDeposits: (filter: { status?: string; email?: string; limit?: number } = {}) =>
    call<{ deposits: AdminDeposit[] }>('listDeposits', filter),

  listUnmatched: (filter: { limit?: number; resolved?: boolean } = {}) =>
    call<{ payments: UnmatchedPayment[] }>('listUnmatchedPayments', filter),

  resolveUnmatched: (id: number) => call<{ result: boolean }>('resolveUnmatchedPayment', { id }),

  getSweepStatus: (providerId?: string) =>
    call<{ chains: SweepStatus[] }>('getSweepStatus', providerId ? { providerId } : {}),

  sweepChain: (providerId: string, hdIndex?: number) =>
    call<{ results: SweepOutcome[]; swept: number; error?: string }>(
      'sweepChain',
      hdIndex === undefined ? { providerId } : { providerId, hdIndex },
    ),

  creditDeposit: (code: string, amountUsdt?: number) =>
    call<{ result: boolean; reason: string; balance: number; points: number }>(
      'adminCreditDeposit',
      amountUsdt === undefined ? { code } : { code, amountUsdt },
    ),
};
